import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface SalesActivityTypeStatus {
  crm_sales_activity_type_status_id: number;
  crm_sales_activity_type_status_key: string;
  crm_sales_activity_type_status_display_name: string;
  is_active: boolean;
  crm_sales_activity_type_id: number;
  tenant_id: number;
}

export interface SalesActivityType {
  crm_sales_activity_type_id: number;
  statuses: SalesActivityTypeStatus[];
  crm_sales_activity_type_key: string;
  crm_sales_activity_type_display_name: string;
  icon?: string | null;
  is_active: boolean;
  tenant_id: number;
}

export interface SalesActivityUser {
  user_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
}

export interface SalesActivityLog {
  crm_sales_activity_log_id: number;
  crm_sales_activity_type: {
    crm_sales_activity_type_id: number;
    crm_sales_activity_type_key: string;
    crm_sales_activity_type_display_name: string;
    icon?: string | null;
  };
  crm_sales_activity_type_status: {
    crm_sales_activity_type_status_id: number;
    crm_sales_activity_type_status_key: string;
    crm_sales_activity_type_status_display_name: string;
  };
  record_id: number;
  notes: string | null;
  is_active: boolean;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  crm_entity_id: number;
  crm_sales_activity_type_id: number;
  crm_sales_activity_type_status_id: number;
  created_by: SalesActivityUser | number;
  updated_by: SalesActivityUser | number;
}

export interface CreateSalesActivityLogPayload {
  tenant_id: number;
  crm_entity_id: number;
  record_id: number;
  crm_sales_activity_type_id: number;
  crm_sales_activity_type_status_id: number;
  notes?: string;
  is_active: boolean;
  created_by?: number;
  updated_by?: number;
}

export interface SalesActivityTypePayload {
  crm_sales_activity_type_key: string;
  crm_sales_activity_type_display_name: string;
  icon?: string;
  tenant_id: number;
  is_active: boolean;
  created_by?: number;
  updated_by?: number;
}

export interface SalesActivityTypeStatusPayload {
  crm_sales_activity_type_status_key: string;
  crm_sales_activity_type_status_display_name: string;
  crm_sales_activity_type_id: number;
  tenant_id: number;
  is_active: boolean;
  created_by?: number;
  updated_by?: number;
}

export const toSalesActivityKey = (displayName: string): string =>
  displayName.trim().toUpperCase().replace(/\s+/g, "_");

export const SalesActivityService = {
  getActivityTypes: async (tenantId: number): Promise<SalesActivityType[]> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-sales-activity-type/?tenant_id=${tenantId}`,
        { headers: getAuthHeaders() }
      );
      return response.data?.results || [];
    } catch (error) {
      console.error("Error fetching sales activity types:", error);
      return handleAuthError(error);
    }
  },

  getActivityLogs: async (
    tenantId: number,
    crmEntityId: number,
    recordId: number
  ): Promise<SalesActivityLog[]> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/crm-sales-activity-log/?tenant_id=${tenantId}&crm_entity_id=${crmEntityId}&record_id=${recordId}&page_size=100`,
        { headers: getAuthHeaders() }
      );
      return response.data?.results || [];
    } catch (error) {
      console.error("Error fetching sales activity logs:", error);
      return handleAuthError(error);
    }
  },

  createActivityLog: async (
    payload: CreateSalesActivityLogPayload
  ): Promise<SalesActivityLog> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/crm-sales-activity-log/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating sales activity log:", error);
      return handleAuthError(error);
    }
  },

  createActivityType: async (
    payload: SalesActivityTypePayload
  ): Promise<SalesActivityType> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/crm-sales-activity-type/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating sales activity type:", error);
      return handleAuthError(error);
    }
  },

  updateActivityType: async (
    id: number,
    payload: Partial<SalesActivityTypePayload>
  ): Promise<SalesActivityType> => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/crm-sales-activity-type/${id}/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating sales activity type:", error);
      return handleAuthError(error);
    }
  },

  deleteActivityType: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_BASE_URL}/crm-sales-activity-type/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting sales activity type:", error);
      return handleAuthError(error);
    }
  },

  createActivityTypeStatus: async (
    payload: SalesActivityTypeStatusPayload
  ): Promise<SalesActivityTypeStatus> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/crm-sales-activity-type-status/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating sales activity type status:", error);
      return handleAuthError(error);
    }
  },

  updateActivityTypeStatus: async (
    id: number,
    payload: Partial<SalesActivityTypeStatusPayload>
  ): Promise<SalesActivityTypeStatus> => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/crm-sales-activity-type-status/${id}/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating sales activity type status:", error);
      return handleAuthError(error);
    }
  },

  deleteActivityTypeStatus: async (id: number): Promise<void> => {
    try {
      await axios.delete(
        `${API_BASE_URL}/crm-sales-activity-type-status/${id}/`,
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error("Error deleting sales activity type status:", error);
      return handleAuthError(error);
    }
  },
};
